import React, { useState, useRef } from 'react';
import { 
  Card, Button, Space, Table, message, Row, Col, 
  InputNumber, Input, Select, Modal, Form, Tag, Alert, Checkbox,
  Tooltip, Divider
} from 'antd';
import { 
  CopyOutlined, DownloadOutlined, WalletOutlined, SendOutlined,
  ReloadOutlined, StopOutlined, DeleteOutlined, EyeOutlined, EyeInvisibleOutlined,
  QuestionCircleOutlined, LinkOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// 接口定义
interface WalletEntry {
  id: string;
  privateKey: string;
  address: string;
  balance?: string;
  status?: 'idle' | 'pending' | 'success' | 'failed' | 'stopped';
}

interface ConfigState {
  rpcUrl: string;
  toAddress: string;
  value: string;
  inputData: string;
  executionCount: number;
  delayBetweenRuns: number;
  delayBetweenWallets: number;
  isBatch: boolean;
  isSelfTransfer: boolean;
  isPolling: boolean;
  is1559: boolean;
  isAdvancedGas: boolean;
  gasLimit: string;
  gasPrice: string;
  maxFeePerGas: string;
  maxPriorityFeePerGas: string;
  addressToReplace: string;
}

interface LogEntry {
  timestamp: string;
  message: string;
  type: 'info' | 'success' | 'error';
}

// 预设网络配置
const NETWORK_CONFIGS = [
  {
    name: 'BNB Smart Chain Mainnet',
    rpcUrl: 'https://bsc-dataseed2.bnbchain.org',
    chainId: 56,
    symbol: 'BNB'
  },
  {
    name: 'Ethereum Mainnet',
    rpcUrl: 'https://eth.llamarpc.com',
    chainId: 1,
    symbol: 'ETH'
  },
  {
    name: 'Polygon Mainnet',
    rpcUrl: 'https://polygon-rpc.com',
    chainId: 137,
    symbol: 'MATIC'
  },
  {
    name: 'Arbitrum One',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    chainId: 42161,
    symbol: 'ETH'
  },
  {
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    chainId: 8453,
    symbol: 'ETH'
  },
];

const BatchMint: React.FC = () => {
  // 状态管理
  const [wallets, setWallets] = useState<WalletEntry[]>([]);
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set());
  const [interactionMode] = useState<'privateKey' | 'metaMask'>('privateKey');
  const [config, setConfig] = useState<ConfigState>({
    rpcUrl: NETWORK_CONFIGS[0].rpcUrl,
    toAddress: '',
    value: '0',
    inputData: '',
    executionCount: 1,
    delayBetweenRuns: 0.1,
    delayBetweenWallets: 0.1,
    isBatch: false,
    isSelfTransfer: false,
    isPolling: false,
    is1559: true,
    isAdvancedGas: false,
    gasLimit: '',
    gasPrice: '',
    maxFeePerGas: '',
    maxPriorityFeePerGas: '',
    addressToReplace: ''
  });
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [shouldStop, setShouldStop] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Modal 状态
  const [isInstructionModalVisible, setIsInstructionModalVisible] = useState(false);
  const [isCopyHashModalVisible, setIsCopyHashModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [hashInput, setHashInput] = useState('');
  const [privateKeysInput, setPrivateKeysInput] = useState('');
  const [showPrivateKeys, setShowPrivateKeys] = useState(false);

  const logsRef = useRef<HTMLDivElement>(null);

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
    border: 'none',
    boxShadow: '0 2px 4px rgba(255, 77, 79, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 工具函数
  const isValidAddress = (address: string): boolean => {
    return ethers.isAddress(address);
  };

  const isValidHex = (hex: string): boolean => {
    return /^0x[0-9a-fA-F]*$/.test(hex);
  };

  const addLog = (message: string, type: LogEntry['type'] = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry: LogEntry = { timestamp, message, type };
    setLogs(prev => [...prev, logEntry]);
    
    // 自动滚动到底部
    setTimeout(() => {
      if (logsRef.current) {
        logsRef.current.scrollTop = logsRef.current.scrollHeight;
      }
    }, 100);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label}已复制到剪贴板`);
  };

  // 导入钱包
  const handleImportWallets = () => {
    if (!privateKeysInput.trim()) {
      message.error('请输入私钥');
      return;
    }

    const privateKeys = privateKeysInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const newWallets: WalletEntry[] = [];
    const invalidKeys: string[] = [];

    privateKeys.forEach((privateKey, index) => {
      try {
        const wallet = new ethers.Wallet(privateKey);
        newWallets.push({
          id: `wallet_${Date.now()}_${index}`,
          address: wallet.address,
          privateKey: privateKey,
          status: 'idle'
        });
      } catch (error) {
        invalidKeys.push(`第${index + 1}行`);
      }
    });

    if (invalidKeys.length > 0) {
      message.error(`发现${invalidKeys.length}个无效私钥: ${invalidKeys.join(', ')}`);
      return;
    }

    if (newWallets.length === 0) {
      message.error('没有有效的私钥');
      return;
    }

    setWallets(prev => [...prev, ...newWallets]);
    setIsImportModalVisible(false);
    setPrivateKeysInput('');
    addLog(`成功导入${newWallets.length}个钱包`, 'success');
    message.success(`成功导入${newWallets.length}个钱包`);
  };

  // 刷新余额
  const refreshBalances = async () => {
    if (wallets.length === 0) {
      message.warning('没有钱包需要刷新');
      return;
    }

    setLoading(true);
    addLog('开始刷新余额...', 'info');

    try {
      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const updatedWallets = [...wallets];

      for (let i = 0; i < updatedWallets.length; i++) {
        const wallet = updatedWallets[i];

        try {
          const balance = await provider.getBalance(wallet.address);
          wallet.balance = ethers.formatEther(balance);
          addLog(`已更新 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 余额`, 'info');
        } catch (error) {
          console.error(`获取 ${wallet.address} 余额失败:`, error);
          addLog(`获取 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 余额失败`, 'error');
        }
      }

      setWallets(updatedWallets);
      addLog('余额刷新完成', 'success');
      message.success('余额刷新完成');
    } catch (error) {
      console.error('刷新余额失败:', error);
      addLog('刷新余额失败', 'error');
      message.error('刷新余额失败');
    } finally {
      setLoading(false);
    }
  };

  // 复制hash功能
  const handleCopyHash = async () => {
    if (!hashInput.trim()) {
      message.error('请输入交易Hash');
      return;
    }

    try {
      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const tx = await provider.getTransaction(hashInput.trim());

      if (!tx) {
        message.error('未找到该交易');
        return;
      }

      // 填充配置
      setConfig(prev => ({
        ...prev,
        toAddress: tx.to || '',
        value: ethers.formatEther(tx.value || 0),
        inputData: tx.data || '0x',
        addressToReplace: tx.to || ''
      }));

      setIsCopyHashModalVisible(false);
      setHashInput('');
      addLog(`成功从交易 ${hashInput.slice(0, 10)}... 复制参数`, 'success');
      message.success('交易参数已复制到配置中');
    } catch (error) {
      console.error('获取交易失败:', error);
      addLog('获取交易失败', 'error');
      message.error('获取交易失败，请检查Hash和网络');
    }
  };

  // 停止任务
  const stopTask = () => {
    setShouldStop(true);
    addLog('用户请求停止任务', 'info');
  };

  // 核心执行任务功能
  const handleExecuteTask = async () => {
    // 启动与验证
    if (selectedWallets.size === 0) {
      message.error('请至少选择一个钱包');
      return;
    }

    if (!config.rpcUrl) {
      message.error('请配置RPC URL');
      return;
    }

    if (!config.isSelfTransfer && !isValidAddress(config.toAddress)) {
      message.error('请输入有效的目标地址');
      return;
    }

    if (!config.inputData || !isValidHex(config.inputData)) {
      message.error('请输入有效的16进制数据');
      return;
    }

    setIsExecuting(true);
    setShouldStop(false);
    addLog('开始执行批量交互任务...', 'info');

    try {
      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const selectedWalletList = wallets.filter(w => selectedWallets.has(w.id));

      // 更新钱包状态为pending
      setWallets(prev => prev.map(w =>
        selectedWallets.has(w.id) ? { ...w, status: 'pending' } : w
      ));

      // 外层循环：遍历钱包
      for (let walletIndex = 0; walletIndex < selectedWalletList.length; walletIndex++) {
        if (shouldStop) {
          addLog('任务已被用户停止', 'info');
          break;
        }

        const wallet = selectedWalletList[walletIndex];
        addLog(`开始处理钱包 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`, 'info');

        try {
          const signer = new ethers.Wallet(wallet.privateKey, provider);

          // 内层循环：按次数执行
          for (let i = 0; i < config.executionCount; i++) {
            if (shouldStop) {
              addLog('任务已被用户停止', 'info');
              break;
            }

            addLog(`钱包 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 第 ${i + 1} 次执行`, 'info');

            try {
              // A. 动态地址替换（仅在高级功能开启时）
              let finalInputData = config.inputData;
              if (config.isAdvancedGas && config.addressToReplace && isValidAddress(config.addressToReplace)) {
                const placeholder = config.addressToReplace.slice(2).toLowerCase();
                const sender = wallet.address.slice(2).toLowerCase();
                
                // 处理32字节对齐的地址格式（去掉0x前缀后的40位地址前面补零到64位）
                const paddedPlaceholder = placeholder.padStart(64, '0');
                const paddedSender = sender.padStart(64, '0');
                
                // 先尝试替换32字节对齐的地址
                let dataToProcess = finalInputData.slice(2).toLowerCase();
                if (dataToProcess.includes(paddedPlaceholder)) {
                  dataToProcess = dataToProcess.replaceAll(paddedPlaceholder, paddedSender);
                  addLog(`地址替换 (32字节): ${config.addressToReplace} -> ${wallet.address}`, 'info');
                } else if (dataToProcess.includes(placeholder)) {
                  // 如果没有找到32字节格式，则尝试原始地址格式
                  dataToProcess = dataToProcess.replaceAll(placeholder, sender);
                  addLog(`地址替换 (20字节): ${config.addressToReplace} -> ${wallet.address}`, 'info');
                } else {
                  addLog(`警告: 在交易数据中未找到地址 ${config.addressToReplace}`, 'error');
                }
                
                finalInputData = '0x' + dataToProcess;
              }

              // B. 决定目标地址
              const finalToAddress = config.isSelfTransfer ? wallet.address : config.toAddress;

              // C. 构建交易对象
              const tx: any = {
                to: finalToAddress,
                value: ethers.parseEther(config.value || '0'),
                data: finalInputData,
              };

              // 添加Gas配置
              if (config.gasLimit) {
                tx.gasLimit = config.gasLimit;
              }

              if (config.is1559) {
                if (config.maxFeePerGas) {
                  tx.maxFeePerGas = ethers.parseUnits(config.maxFeePerGas, 'gwei');
                }
                if (config.maxPriorityFeePerGas) {
                  tx.maxPriorityFeePerGas = ethers.parseUnits(config.maxPriorityFeePerGas, 'gwei');
                }
              } else {
                if (config.gasPrice) {
                  tx.gasPrice = ethers.parseUnits(config.gasPrice, 'gwei');
                }
              }

              // D. 发送交易
              const txResponse = await signer.sendTransaction(tx);
              addLog(`交易已发送: ${txResponse.hash}`, 'success');

              // 等待确认
              await txResponse.wait();
              addLog(`交易已确认: ${txResponse.hash}`, 'success');

            } catch (error: any) {
              const errorMsg = error.message || error.toString();
              addLog(`交易失败: ${errorMsg}`, 'error');

              if (!config.isPolling) {
                // 如果不是轮询模式，遇到错误就停止该钱包的后续执行
                break;
              }
            }

            // E. 处理"次数间"延迟
            if (i < config.executionCount - 1 && !config.isBatch && config.delayBetweenRuns > 0) {
              addLog(`等待 ${config.delayBetweenRuns} 秒...`, 'info');
              await new Promise(resolve => setTimeout(resolve, config.delayBetweenRuns * 1000));
            }
          }

          // 更新钱包状态为成功
          setWallets(prev => prev.map(w =>
            w.id === wallet.id ? { ...w, status: 'success' } : w
          ));

        } catch (error: any) {
          const errorMsg = error.message || error.toString();
          addLog(`钱包 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 处理失败: ${errorMsg}`, 'error');

          // 更新钱包状态为失败
          setWallets(prev => prev.map(w =>
            w.id === wallet.id ? { ...w, status: 'failed' } : w
          ));
        }

        // 处理"地址间"延迟
        if (walletIndex < selectedWalletList.length - 1 && !config.isBatch && config.delayBetweenWallets > 0) {
          addLog(`钱包间等待 ${config.delayBetweenWallets} 秒...`, 'info');
          await new Promise(resolve => setTimeout(resolve, config.delayBetweenWallets * 1000));
        }
      }

      addLog('所有任务已完成', 'success');
      message.success('批量交互任务完成');

    } catch (error: any) {
      const errorMsg = error.message || error.toString();
      addLog(`任务执行失败: ${errorMsg}`, 'error');
      message.error('任务执行失败');
    } finally {
      setIsExecuting(false);
      setShouldStop(false);

      // 重置未完成的钱包状态
      setWallets(prev => prev.map(w =>
        w.status === 'pending' ? { ...w, status: shouldStop ? 'stopped' : 'idle' } : w
      ));
    }
  };

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        批量交互工具
      </h1>

      {/* 顶部操作按钮 */}
      <Card className="spacing-responsive">
        <Space wrap>
          <Button
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsInstructionModalVisible(true)}
            style={buttonStyle}
          >
            使用说明
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshBalances}
            loading={loading}
            style={buttonStyle}
          >
            刷新余额
          </Button>
          <Button
            icon={<LinkOutlined />}
            onClick={() => setIsCopyHashModalVisible(true)}
            style={buttonStyle}
          >
            复制hash
          </Button>
          <Button
            icon={<WalletOutlined />}
            onClick={() => setIsImportModalVisible(true)}
            style={buttonStyle}
          >
            导入钱包
          </Button>

        </Space>
      </Card>

      {/* 中间配置区域 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          交易配置
        </h3>

        {/* 网络配置 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                选择网络
              </h4>
              <Select
                value={config.rpcUrl}
                onChange={(value) => setConfig(prev => ({ ...prev, rpcUrl: value }))}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="选择网络"
              >
                {NETWORK_CONFIGS.map(network => (
                  <Select.Option key={network.rpcUrl} value={network.rpcUrl}>
                    {network.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                自定义RPC
              </h4>
              <Input
                value={config.rpcUrl}
                onChange={(e) => setConfig(prev => ({ ...prev, rpcUrl: e.target.value }))}
                style={componentStyle}
                placeholder="输入自定义RPC URL"
              />
            </div>
          </Col>
        </Row>

        {/* 模式切换 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                执行模式
              </h4>
              <Space wrap>
                <Checkbox
                  checked={config.isBatch}
                  onChange={(e) => setConfig(prev => ({ ...prev, isBatch: e.target.checked }))}
                >
                  批量模式
                </Checkbox>
                <Checkbox
                  checked={config.isSelfTransfer}
                  onChange={(e) => setConfig(prev => ({ ...prev, isSelfTransfer: e.target.checked }))}
                >
                  自我转账
                </Checkbox>
                <Checkbox
                  checked={config.isPolling}
                  onChange={(e) => setConfig(prev => ({ ...prev, isPolling: e.target.checked }))}
                >
                  轮询模式
                </Checkbox>
                <Checkbox
                  checked={config.is1559}
                  onChange={(e) => setConfig(prev => ({ ...prev, is1559: e.target.checked }))}
                >
                  EIP-1559
                </Checkbox>
                <Checkbox
                  checked={config.isAdvancedGas}
                  onChange={(e) => setConfig(prev => ({ ...prev, isAdvancedGas: e.target.checked }))}
                >
                  替换交易发起方
                </Checkbox>
              </Space>
            </div>
          </Col>
        </Row>

        {/* 主要参数 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                To地址
              </h4>
              <Input
                value={config.toAddress}
                onChange={(e) => setConfig(prev => ({ ...prev, toAddress: e.target.value }))}
                style={componentStyle}
                placeholder="目标合约地址"
                disabled={config.isSelfTransfer}
              />
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Value (ETH)
              </h4>
              <Input
                value={config.value}
                onChange={(e) => setConfig(prev => ({ ...prev, value: e.target.value }))}
                style={componentStyle}
                placeholder="发送的主币数量"
              />
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                执行次数
              </h4>
              <InputNumber
                value={config.executionCount}
                onChange={(value) => setConfig(prev => ({ ...prev, executionCount: value || 1 }))}
                min={1}
                max={1000}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="每个钱包执行次数"
              />
            </div>
          </Col>
        </Row>

        {/* 延迟配置 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                延迟-次数间 (秒)
              </h4>
              <InputNumber
                value={config.delayBetweenRuns}
                onChange={(value) => setConfig(prev => ({ ...prev, delayBetweenRuns: value || 0 }))}
                min={0}
                max={3600}
                step={0.1}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="每次执行间隔"
                disabled={config.isBatch}
              />
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                延迟-地址间 (秒)
              </h4>
              <InputNumber
                value={config.delayBetweenWallets}
                onChange={(value) => setConfig(prev => ({ ...prev, delayBetweenWallets: value || 0 }))}
                min={0}
                max={3600}
                step={0.1}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="钱包间隔"
                disabled={config.isBatch}
              />
            </div>
          </Col>
        </Row>

        {/* Gas配置 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Gas Limit
              </h4>
              <Input
                value={config.gasLimit}
                onChange={(e) => setConfig(prev => ({ ...prev, gasLimit: e.target.value }))}
                style={componentStyle}
                placeholder="Gas限制"
              />
            </div>
          </Col>

          {!config.is1559 && (
            <Col xs={24} sm={8}>
              <div>
                <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Gas Price (Gwei)
                </h4>
                <Input
                  value={config.gasPrice}
                  onChange={(e) => setConfig(prev => ({ ...prev, gasPrice: e.target.value }))}
                  style={componentStyle}
                  placeholder="Gas价格"
                />
              </div>
            </Col>
          )}

          {config.is1559 && (
            <>
              <Col xs={24} sm={8}>
                <div>
                  <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Max Fee Per Gas (Gwei)
                  </h4>
                  <Input
                    value={config.maxFeePerGas}
                    onChange={(e) => setConfig(prev => ({ ...prev, maxFeePerGas: e.target.value }))}
                    style={componentStyle}
                    placeholder="最大费用"
                  />
                </div>
              </Col>

              <Col xs={24} sm={8}>
                <div>
                  <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Max Priority Fee (Gwei)
                  </h4>
                  <Input
                    value={config.maxPriorityFeePerGas}
                    onChange={(e) => setConfig(prev => ({ ...prev, maxPriorityFeePerGas: e.target.value }))}
                    style={componentStyle}
                    placeholder="优先费用"
                  />
                </div>
              </Col>
            </>
          )}
        </Row>

        {/* 动态数据区域 */}
        <Divider>动态数据配置</Divider>

        {config.isAdvancedGas && (
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                      <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                需要被替换的地址
              </h4>
              <Input
                value={config.addressToReplace}
                onChange={(e) => setConfig(prev => ({ ...prev, addressToReplace: e.target.value }))}
                style={componentStyle}
                placeholder="输入需要替换为发送方地址的地址，如：0x1234..."
              />
            </div>
          </Col>

            <Col xs={24} sm={12}>
              <div style={{ paddingTop: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
                  <strong>使用说明：</strong><br/>
                  1. 在此输入框中填入需要被替换的地址<br/>
                  2. 在下方交易数据中使用该地址<br/>
                  3. 执行时会自动替换为各钱包的实际地址
                </div>
              </div>
            </Col>
          </Row>
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                填写16进制数据 *
              </h4>
              <Input.TextArea
                value={config.inputData}
                onChange={(e) => setConfig(prev => ({ ...prev, inputData: e.target.value }))}
                style={{ ...componentStyle, fontFamily: 'monospace' }}
                placeholder="输入完整的16进制交易数据，例如：0x1234abcd..."
                rows={4}
              />
            </div>
          </Col>
        </Row>

        {/* 执行按钮区域 */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Space size="large">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleExecuteTask}
              disabled={selectedWallets.size === 0 || isExecuting}
              loading={isExecuting}
              style={primaryButtonStyle}
              size="large"
            >
              执行任务
            </Button>
            {isExecuting && (
              <Button
                icon={<StopOutlined />}
                onClick={stopTask}
                style={dangerButtonStyle}
                size="large"
              >
                停止任务
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* 钱包表格区域 */}
      {wallets.length > 0 && (
        <Card className="spacing-responsive">
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
              钱包列表 ({wallets.length}个)
            </h3>
            <Space>
              <Button
                size="small"
                icon={showPrivateKeys ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setShowPrivateKeys(!showPrivateKeys)}
                style={buttonStyle}
              >
                {showPrivateKeys ? '隐藏' : '显示'}私钥
              </Button>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setWallets([]);
                  setSelectedWallets(new Set());
                  addLog('已清空所有钱包', 'info');
                }}
                style={buttonStyle}
              >
                清空
              </Button>
            </Space>
          </div>

          <Table
            columns={[
              {
                title: '序号',
                dataIndex: 'index',
                width: 60,
                render: (_, __, index) => index + 1,
              },
              {
                title: '地址',
                dataIndex: 'address',
                width: 200,
                render: (text: string) => (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {text.slice(0, 6)}...{text.slice(-4)}
                    </span>
                    <Tooltip title="复制地址">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(text, '地址')}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: '私钥',
                dataIndex: 'privateKey',
                width: 200,
                render: (text: string) => (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {showPrivateKeys ? `${text.slice(0, 10)}...${text.slice(-4)}` : '••••••••••••••••'}
                    </span>
                    <Tooltip title="复制私钥">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(text, '私钥')}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: '余额',
                dataIndex: 'balance',
                width: 120,
                render: (text: string) => text || '-',
              },
              {
                title: '状态',
                dataIndex: 'status',
                width: 80,
                render: (status: string) => {
                  const statusConfig = {
                    idle: { color: 'default', text: '待处理' },
                    pending: { color: 'processing', text: '处理中' },
                    success: { color: 'success', text: '成功' },
                    failed: { color: 'error', text: '失败' },
                    stopped: { color: 'warning', text: '已停止' }
                  };
                  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.idle;
                  return <Tag color={config.color}>{config.text}</Tag>;
                },
              },
            ]}
            dataSource={wallets}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: Array.from(selectedWallets),
              onChange: (selectedRowKeys) => {
                setSelectedWallets(new Set(selectedRowKeys as string[]));
              },
              onSelectAll: (selected) => {
                if (selected) {
                  setSelectedWallets(new Set(wallets.map(w => w.id)));
                } else {
                  setSelectedWallets(new Set());
                }
              },
            }}
            pagination={false}
            scroll={{ x: 800, y: 400 }}
            size="small"
          />

          {/* 表格底部汇总 */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f5f5f5',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <Row gutter={16}>
              <Col span={8}>
                <strong>总选中: {selectedWallets.size} 个</strong>
              </Col>
              <Col span={8}>
                <strong>总余额: {
                  wallets
                    .filter(w => selectedWallets.has(w.id))
                    .reduce((sum, w) => sum + (parseFloat(w.balance || '0')), 0)
                    .toFixed(6)
                } ETH</strong>
              </Col>
              <Col span={8}>
                <strong>预计交易: {selectedWallets.size * config.executionCount} 笔</strong>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {/* 日志区域 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          执行日志
        </h3>
        <div
          ref={logsRef}
          style={{
            height: '200px',
            overflow: 'auto',
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        >
          {logs.length === 0 ? (
            <div style={{ color: '#999', textAlign: 'center', paddingTop: '60px' }}>
              暂无日志
            </div>
          ) : (
            logs.map((log, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '4px',
                  color: log.type === 'error' ? '#ff4d4f' : log.type === 'success' ? '#52c41a' : '#666'
                }}
              >
                [{log.timestamp}] {log.message}
              </div>
            ))
          )}
        </div>
        <div style={{ marginTop: '8px', textAlign: 'right' }}>
          <Button
            size="small"
            onClick={() => setLogs([])}
            style={buttonStyle}
          >
            清空日志
          </Button>
        </div>
      </Card>

      {/* 使用说明模态框 */}
      <Modal
        title="使用说明"
        open={isInstructionModalVisible}
        onCancel={() => setIsInstructionModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsInstructionModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          <h4>🎯 工具概述</h4>
          <p>批量交互工具允许您通过发送原始16进制数据来执行复杂的链上交易，支持多种执行模式和动态地址替换。</p>

          <h4>📋 使用步骤</h4>
          <ol>
            <li><strong>导入钱包</strong>：批量导入私钥</li>
            <li><strong>配置网络</strong>：选择目标区块链网络</li>
            <li><strong>设置参数</strong>：配置目标地址、Value、执行次数等</li>
            <li><strong>输入数据</strong>：填写16进制交易数据</li>
            <li><strong>动态替换</strong>：设置需要替换的占位符地址</li>
            <li><strong>执行任务</strong>：开始批量交易</li>
          </ol>

          <h4>🔧 执行模式</h4>
          <ul>
            <li><strong>批量模式</strong>：忽略延迟设置，快速执行</li>
            <li><strong>自我转账</strong>：目标地址自动设为发送方地址</li>
            <li><strong>轮询模式</strong>：遇到错误时继续重试</li>
            <li><strong>EIP-1559</strong>：使用新的Gas费用机制，显示Max Fee和Priority Fee设置</li>
            <li><strong>替换交易发起方</strong>：启用动态地址替换功能</li>
          </ul>

          <h4>🔄 动态地址替换</h4>
          <p>启用"替换交易发起方"后，可以在16进制数据中使用占位符地址，系统会自动替换为实际的发送方地址。</p>
          <ul>
            <li><strong>使用场景</strong>：mint代币、NFT等需要将资产发送给交易发起者的操作</li>
            <li><strong>支持格式</strong>：自动识别20字节和32字节对齐的地址格式</li>
            <li><strong>替换原理</strong>：将交易数据中的占位符地址替换为当前钱包地址</li>
          </ul>

          <h4>⚠️ 安全提醒</h4>
          <ul>
            <li>请确保16进制数据的正确性</li>
            <li>建议先用小额测试</li>
            <li>私钥仅在本地处理，不会上传</li>
          </ul>
        </div>
      </Modal>

      {/* 复制hash模态框 */}
      <Modal
        title="从交易Hash复制参数"
        open={isCopyHashModalVisible}
        onOk={handleCopyHash}
        onCancel={() => {
          setIsCopyHashModalVisible(false);
          setHashInput('');
        }}
        width={600}
        okText="获取参数"
        cancelText="取消"
      >
        <Alert
          message="从现有交易复制参数"
          description="输入交易Hash，系统会自动获取交易参数并填充到配置中。"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="vertical">
          <Form.Item
            label="交易Hash"
            required
          >
            <Input
              value={hashInput}
              onChange={(e) => setHashInput(e.target.value)}
              placeholder="输入交易Hash，例如：0x1234567890abcdef..."
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入钱包模态框 */}
      <Modal
        title="导入钱包私钥"
        open={isImportModalVisible}
        onOk={handleImportWallets}
        onCancel={() => {
          setIsImportModalVisible(false);
          setPrivateKeysInput('');
        }}
        width={600}
        okText="确认导入"
        cancelText="取消"
      >
        <Alert
          message="安全提醒"
          description="私钥仅在本地浏览器处理，不会上传或存储到服务器。页面关闭后私钥信息将从内存中清除。"
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="vertical">
          <Form.Item
            label="私钥列表 (每行一个私钥)"
            required
          >
            <Input.TextArea
              value={privateKeysInput}
              onChange={(e) => setPrivateKeysInput(e.target.value)}
              placeholder="请粘贴私钥，每行一个&#10;例如：&#10;0x1234567890abcdef...&#10;0xabcdef1234567890..."
              rows={8}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 安全提示 */}
      <Card size="small" style={{
        background: '#fff2f0',
        border: '1px solid #ffccc7',
        marginTop: '20px'
      }}>
        <div style={{ color: '#ff4d4f' }}>
          <strong>⚠️ 安全提醒：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>此工具面向高级用户，请确保您了解16进制数据的含义</li>
            <li>请仔细验证交易数据的正确性，错误的数据可能导致资产损失</li>
            <li>建议先用小额测试，确认无误后再进行大额操作</li>
            <li>私钥仅在本地浏览器处理，不会上传到服务器</li>
            <li>动态地址替换功能请谨慎使用，确保占位符地址正确</li>
            <li>轮询模式会在遇到错误时继续重试，请注意Gas消耗</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default BatchMint;
